import { ProductNames } from '../../models/product';
import {
  AccountSelectionResponse,
  CredentialsEntity,
  ResetCredentialsResponse,
} from '../../models/credentials';
import { base, createApiURL, ResponseObj } from '../../lib-api/services/base';
import { RefreshableItem } from '../../models/refreshableItem';
import { ajaxRetry as ajaxRetryImport } from '../../lib-api/ajaxRetry';
import { uuid } from '../../utilities/utils';

export enum AuthenticationOptionsGroup {
  SE_MOBILE_BANKID_DEVICE_CHOICE = 'SE_MOBILE_BANKID_DEVICE_CHOICE',
}

export enum AuthenticationOptionDefinition {
  SE_MOBILE_BANKID_OTHER_DEVICE = 'SE_MOBILE_BANKID_OTHER_DEVICE',
  SE_MOBILE_BANKID_SAME_DEVICE = 'SE_MOBILE_BANKID_SAME_DEVICE',
}

export type AuthenticationOption = {
  authenticationOptionsGroup: AuthenticationOptionsGroup;
  authenticationOptionDefinition: AuthenticationOptionDefinition;
};

export type CredentialsRequest = {
  providerName: string;
  fields: Record<string, string>;
  callbackUri?: string;
  appUri?: string;
  triggerRefresh?: boolean;
  productNames: ProductNames[];
  selectedAuthenticationOptions?: AuthenticationOption[];
};

export type CreateCredentialsRequest = {
  request: CredentialsRequest;
  refreshableItems: RefreshableItem[];
};

export type AuthenticateCredentialsRequest = {
  id: string;
  request: Pick<CredentialsRequest, 'appUri' | 'callbackUri' | 'productNames'>;
};

export type RefreshCredentialsRequest = {
  id: string;
  authenticate: boolean;
  request: Pick<
    CredentialsRequest,
    'appUri' | 'callbackUri' | 'productNames' | 'selectedAuthenticationOptions'
  >;
  refreshableItems?: RefreshableItem[];
};

export type SelectedAccount = {
  id: string;
};

export type CredentialsClient = {
  get: (id: string, query: string) => Promise<ResponseObj<CredentialsEntity | undefined>>;
  getQR: (id: string) => Promise<ResponseObj<string | undefined>>;
  supplement: (
    id: string,
    information: Record<string, unknown> | null
  ) => Promise<ResponseObj<void>>;
  create: (
    request: CreateCredentialsRequest['request'],
    query: string
  ) => Promise<ResponseObj<CredentialsEntity | undefined>>;
  update: (
    id: string,
    request: CredentialsRequest
  ) => Promise<ResponseObj<CredentialsEntity | undefined>>;
  refresh: (
    request: Pick<RefreshCredentialsRequest, 'id' | 'request'>,
    query: string
  ) => Promise<ResponseObj<null>>;
  authenticate: ({ id, request }: AuthenticateCredentialsRequest) => Promise<ResponseObj<null>>;
  resetCredentialsStatus: (
    id: string
  ) => Promise<ResponseObj<ResetCredentialsResponse | undefined>>;
  getCredentialsResetStatus: (
    id: string
  ) => Promise<ResponseObj<ResetCredentialsResponse | undefined>>;
  supplementalInfoApiCallback: (
    supplementalInfoState: string
  ) => Promise<ResponseObj<string | undefined>>;
  delete: (credentialsId: string) => Promise<ResponseObj<null>>;
  getAccountsSelection: (id: string) => Promise<ResponseObj<AccountSelectionResponse[]>>;
  postAccountSelection: (
    id: string,
    selectedAccounts: SelectedAccount[]
  ) => Promise<ResponseObj<void>>;
};

/** istanbul ignore next: this is a client see ADR.
 * @module adrs/001-clients-services.md
 * */
export const createCredentialsClient = (ajaxRetry = ajaxRetryImport): CredentialsClient => ({
  get(id, query) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/${id}?${query}`),
    });
  },

  getQR(id) {
    return ajaxRetry({
      headers: { ...base.headers(), Accept: 'text/html' },
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/${id}/qr`),
    });
  },

  supplement(id, information) {
    return ajaxRetry({
      data: { information },
      headers: base.headers(),
      method: 'POST',
      url: createApiURL(`/api/v1/credentials/${id}/supplemental-information`),
    });
  },

  create(request, query) {
    return ajaxRetry({
      data: request,
      headers: base.headers(),
      method: 'POST',
      url: createApiURL(`/api/v1/credentials?${query}`),
    });
  },

  update(id, request) {
    return ajaxRetry({
      data: request,
      headers: base.headers(),
      method: 'PUT',
      url: createApiURL(`/api/v1/credentials/${id}`),
    });
  },

  refresh({ id, request }, query) {
    return ajaxRetry({
      data: request,
      headers: base.headers(),
      method: 'POST',
      url: createApiURL(`/api/v1/credentials/${id}/refresh?${query}`),
    });
  },

  authenticate({ id, request }) {
    return ajaxRetry({
      data: request,
      headers: base.headers(),
      method: 'POST',
      url: createApiURL(`/api/v1/credentials/${id}/authenticate`),
    });
  },

  resetCredentialsStatus(credentialsId) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'POST',
      url: createApiURL(`/api/v1/credentials/operations/abort?credentials_id=${credentialsId}`),
    });
  },

  getCredentialsResetStatus(credentialsId) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/operations/abort?credentials_id=${credentialsId}`),
    });
  },

  supplementalInfoApiCallback(supplementalInfoState) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/third-party/callback?state=${supplementalInfoState}`),
    });
  },
  delete(credentialsId: string) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'DELETE',
      url: createApiURL(`/api/v1/credentials/${credentialsId}`),
    });
  },
  getAccountsSelection(id) {
    return ajaxRetry({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/${id}/accounts-selection`),
    });
  },
  postAccountSelection(id, selectedAccounts) {
    return ajaxRetry({
      data: { selectedAccounts },
      headers: {
        ...base.headers(),
        'Idempotency-Key': uuid(),
      },
      method: 'POST',
      url: createApiURL(`/api/v1/credentials/${id}:select-accounts`),
    });
  },
});

export const credentialsClient = createCredentialsClient();
